import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { adminService } from '../../services/api';

const {
  FiKey, FiPlus, FiEdit2, FiCheck, FiX, FiTrash2,
  FiToggleLeft, FiToggleRight, FiAlertCircle, FiCheckCircle,
  FiRefreshCw, FiCopy, FiShuffle
} = FiIcons;

const RedemptionCodeManager = ({ adminUserId, codes = [], onRefresh }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Form states
  const [codeStr, setCodeStr] = useState('');
  const [grantType, setGrantType] = useState('single');
  const [singleAmt, setSingleAmt] = useState(1);
  const [maxUses, setMaxUses] = useState(1);
  const [notes, setNotes] = useState('');

  const resetForm = () => {
    setCodeStr('');
    setGrantType('single');
    setSingleAmt(1);
    setMaxUses(1);
    setNotes('');
    setShowAddForm(false);
    setEditingCode(null);
    setError(null);
  };

  const showNotification = (msg, isErr = false) => {
    if (isErr) {
      setError(msg);
      setTimeout(() => setError(null), 5000);
    } else {
      setSuccess(msg);
      setTimeout(() => setSuccess(null), 4000);
    }
  };

  const generateRandomCode = () => {
    // Unambiguous easy-to-read letters and numbers (excluding 0, O, 1, I, L)
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    const getChunk = (length = 4) => {
      let chunk = '';
      for (let i = 0; i < length; i++) {
        chunk += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return chunk;
    };

    const existing = new Set(codes.map(c => (c.code || '').toUpperCase()));
    let newCode = '';
    let attempts = 0;

    do {
      newCode = `GWA-${getChunk(4)}-${getChunk(4)}`;
      attempts++;
    } while (existing.has(newCode) && attempts < 20);

    setCodeStr(newCode);
  };

  const handleCopy = (code, id) => {
    navigator.clipboard?.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!codeStr.trim()) {
      showNotification('Access code cannot be empty.', true);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      if (editingCode) {
        const res = await adminService.updateAccessCode(adminUserId, {
          code_id: editingCode.id,
          code: codeStr.trim().toUpperCase(),
          grant_type: grantType,
          single_amt_grant: parseInt(singleAmt, 10) || 1,
          max_uses: parseInt(maxUses, 10) || 1,
          is_active: editingCode.is_active,
          notes: notes.trim()
        });
        if (res.success) {
          showNotification(res.message || 'Code updated successfully.');
          resetForm();
          onRefresh?.();
        } else {
          throw new Error(res.error || 'Failed to update code.');
        }
      } else {
        const res = await adminService.addAccessCode(adminUserId, {
          code: codeStr.trim().toUpperCase(),
          grant_type: grantType,
          single_amt_grant: parseInt(singleAmt, 10) || 1,
          max_uses: parseInt(maxUses, 10) || 1,
          notes: notes.trim()
        });
        if (res.success) {
          showNotification(res.message || 'Code created successfully.');
          resetForm();
          onRefresh?.();
        } else {
          throw new Error(res.error || 'Failed to add code.');
        }
      }
    } catch (err) {
      showNotification(err.message, true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (c) => {
    const nextState = c.is_active === 1 ? 0 : 1;
    try {
      const res = await adminService.toggleAccessCode(adminUserId, c.id, nextState);
      if (res.success) {
        showNotification(`Code ${c.code} is now ${nextState === 1 ? 'ACTIVE' : 'DISABLED'}.`);
        onRefresh?.();
      }
    } catch (err) {
      showNotification(err.message, true);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Permanently delete code "${c.code}"?`)) return;
    try {
      const res = await adminService.deleteAccessCode(adminUserId, c.id);
      if (res.success) {
        showNotification(`Code "${c.code}" deleted.`);
        onRefresh?.();
      }
    } catch (err) {
      showNotification(err.message, true);
    }
  };

  const startEdit = (c) => {
    setEditingCode(c);
    setCodeStr(c.code);
    setGrantType(c.grant_type || 'single');
    setSingleAmt(c.single_amt_grant || 1);
    setMaxUses(c.max_uses || 1);
    setNotes(c.notes || '');
    setShowAddForm(true);
  };

  return (
    <div className="bg-white rounded-2xl border border-purple-200/80 p-6 shadow-sm mb-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-purple-100 pb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiKey} className="text-xl" />
          </div>
          <div>
            <h3 className="text-base font-bold text-slate-800">Redemption Codes Management</h3>
            <p className="text-xs text-slate-500">
              Manage one-time or multi-use access codes stored in the database.
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {!showAddForm && (
            <button
              onClick={() => { resetForm(); setShowAddForm(true); }}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold uppercase tracking-wider flex items-center space-x-1.5 shadow-sm transition-all"
            >
              <SafeIcon icon={FiPlus} />
              <span>Create New Code</span>
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center space-x-2"
          >
            <SafeIcon icon={FiCheckCircle} className="text-emerald-600 text-base flex-shrink-0" />
            <span>{success}</span>
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-xl text-xs font-semibold flex items-center space-x-2"
          >
            <SafeIcon icon={FiAlertCircle} className="text-red-600 text-base flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit Form Modal / Panel */}
      {showAddForm && (
        <motion.form
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          onSubmit={handleSave}
          className="bg-slate-50/80 border border-purple-200 rounded-xl p-5 space-y-4"
        >
          <div className="flex items-center justify-between border-b border-slate-200 pb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-purple-900">
              {editingCode ? `Edit Access Code (#${editingCode.id})` : 'Add New Access Code'}
            </span>
            <button
              type="button"
              onClick={resetForm}
              className="text-slate-400 hover:text-slate-600 p-1"
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[11px] font-bold text-slate-600 uppercase">
                  Code Identifier *
                </label>
                <button
                  type="button"
                  onClick={generateRandomCode}
                  className="text-[10px] font-bold text-purple-600 hover:text-purple-800 flex items-center space-x-1 uppercase tracking-wider transition-colors"
                  title="Generate random code"
                >
                  <SafeIcon icon={FiShuffle} />
                  <span>Generate Code</span>
                </button>
              </div>
              <div className="flex space-x-1.5">
                <input
                  type="text"
                  required
                  value={codeStr}
                  onChange={e => setCodeStr(e.target.value.toUpperCase())}
                  placeholder="GWA-XXXX-XXXX"
                  className="w-full px-3 py-2 bg-white rounded-lg border border-slate-200 text-xs font-mono font-bold uppercase focus:outline-none focus:border-purple-600"
                />
                <button
                  type="button"
                  onClick={generateRandomCode}
                  className="px-2.5 py-2 bg-purple-100 hover:bg-purple-200 text-purple-800 border border-purple-200 rounded-lg text-xs font-bold flex items-center space-x-1 transition-colors flex-shrink-0"
                  title="Generate a unique readable code"
                >
                  <SafeIcon icon={FiShuffle} />
                  <span className="hidden lg:inline text-[11px]">Generate</span>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                Target Tier Granted
              </label>
              <select
                value={grantType}
                onChange={e => setGrantType(e.target.value)}
                className="w-full px-3 py-2 bg-white rounded-lg border border-slate-200 text-xs font-bold focus:outline-none focus:border-purple-600"
              >
                <option value="single">Single (Adds Conversions)</option>
                <option value="trial">Trial Access</option>
                <option value="lifetime">Lifetime Access</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                Conversions to Add
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={singleAmt}
                onChange={e => setSingleAmt(e.target.value)}
                className="w-full px-3 py-2 bg-white rounded-lg border border-slate-200 text-xs font-bold focus:outline-none focus:border-purple-600"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
                Max Uses (1 = One-Time)
              </label>
              <input
                type="number"
                min="1"
                max="9999"
                value={maxUses}
                onChange={e => setMaxUses(e.target.value)}
                className="w-full px-3 py-2 bg-white rounded-lg border border-slate-200 text-xs font-bold focus:outline-none focus:border-purple-600"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase mb-1">
              Internal Notes / Campaign Name (Optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g., Given to author from beta launch webinar"
              className="w-full px-3 py-2 bg-white rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-purple-600"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold uppercase text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-bold uppercase tracking-wider flex items-center space-x-1.5 shadow-sm disabled:opacity-50"
            >
              <SafeIcon icon={FiCheck} />
              <span>{loading ? 'Saving...' : editingCode ? 'Update Code' : 'Save Code'}</span>
            </button>
          </div>
        </motion.form>
      )}

      {/* Access Codes Table */}
      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        {codes.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <SafeIcon icon={FiKey} className="text-2xl mx-auto mb-2 opacity-40" />
            <p className="text-xs font-medium">No redemption codes found in the database. Click "Create New Code" above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200 text-[10px]">
                <tr>
                  <th className="py-3 px-4">Code</th>
                  <th className="py-3 px-4">Grants</th>
                  <th className="py-3 px-4 text-center">Usage</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Notes</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {codes.map(c => {
                  const isExhausted = c.max_uses > 0 && c.times_used >= c.max_uses;
                  const isActive = c.is_active === 1;

                  return (
                    <tr key={c.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="py-3 px-4 font-mono font-bold text-slate-900">
                        <div className="flex items-center space-x-2">
                          <span>{c.code}</span>
                          <button
                            onClick={() => handleCopy(c.code, c.id)}
                            className="text-slate-300 hover:text-purple-600 p-1"
                            title="Copy code"
                          >
                            <SafeIcon icon={copiedId === c.id ? FiCheck : FiCopy} />
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="capitalize font-bold text-purple-950">{c.grant_type}</span>
                        {c.grant_type === 'single' && (
                          <span className="text-[10px] text-slate-400 ml-1 font-medium">
                            (+{c.single_amt_grant} conversion{c.single_amt_grant > 1 ? 's' : ''})
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center font-bold">
                        <span className={isExhausted ? 'text-amber-600' : 'text-slate-700'}>
                          {c.times_used} / {c.max_uses}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {isExhausted ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200">
                            Redeemed
                          </span>
                        ) : isActive ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-red-100 text-red-800 border border-red-200">
                            Disabled
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-slate-400 max-w-[200px] truncate text-[11px]">
                        {c.notes || '—'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <button
                            onClick={() => handleToggle(c)}
                            title={isActive ? 'Disable Code' : 'Enable Code'}
                            className={`p-1.5 rounded-lg transition-colors ${
                              isActive ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'
                            }`}
                          >
                            <SafeIcon icon={isActive ? FiToggleRight : FiToggleLeft} className="text-lg" />
                          </button>
                          <button
                            onClick={() => startEdit(c)}
                            title="Edit Code"
                            className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          >
                            <SafeIcon icon={FiEdit2} />
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            title="Delete Code"
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RedemptionCodeManager;